import { BarChart } from '@mantine/charts';
import { Badge, Box, Container, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Contract } from '@/api/generated/models';
import { useGetBoardPmSummary } from '@/api/generated/board/board';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { useListObjects } from '@/api/generated/objects/objects';
import {
  IconFigmaCalendar,
  IconFigmaCheckCircle,
  IconFigmaFile,
  IconFigmaStructure
} from '@/assets/icons';
import { CardSection, DateRangeField, MobileListFilters, SelectField } from '@/components/common';
import { buildPeriodOptions, workPackageStatusLabelKey } from '@/features/board/boardDisplay';
import { BoardKpiCard } from '@/features/board/components/BoardKpiCard';
import { ContractRealizationTable } from '@/features/board/components/ContractRealizationTable';
import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';

const formatHours = (value: number) =>
  `${(value ?? 0).toLocaleString('hr-HR', { maximumFractionDigits: 0 })} h`;

const WEEK_MIN_WIDTH = 90;

// Isti chart/state tokeni kao Figma "Status Radnih paketa" (isti redoslijed kao WP_STATUS_ORDER).
// "late" nije pravi status u bazi — backend dijeli 'active' u 'active'/'late' po valid_to (vidi
// BoardDaoImpl.findWorkPackageStatusCounts); "cancelled" se namjerno ne prikazuje (nije dio
// operativnog portfelja koji ovaj graf prikazuje).
const WP_STATUS_ORDER = ['draft', 'active', 'late', 'completed'] as const;
const WP_STATUS_COLORS: Record<(typeof WP_STATUS_ORDER)[number], string> = {
  draft: 'var(--chart-wp-planned)',
  active: 'var(--chart-collected)',
  late: 'var(--chart-wp-late)',
  completed: 'var(--chart-realization-good)'
};

interface WeekMetaEntry {
  label: string;
  percentLabel: string;
  weekYear: string;
}

interface WeekTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  weekMeta: Record<string, WeekMetaEntry>;
}

/** Prilagođeni X-os tick — tjedan (medium, primarna boja) + postotak ispod (regular, muted), kao u Figmi. */
const WeekTick = ({ x, y, payload, weekMeta }: WeekTickProps) => {
  const meta = payload?.value ? weekMeta[payload.value] : undefined;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={12}
        textAnchor="middle"
        fontSize={10}
        fontWeight={500}
        fill="var(--color-text-primary)"
      >
        {meta?.label}
      </text>
      <text dy={26} textAnchor="middle" fontSize={10} fill="var(--color-text-muted)">
        {meta?.percentLabel}
      </text>
    </g>
  );
};

interface WeeklyTooltipPayloadEntry {
  dataKey?: string | number;
  value?: number;
}

interface WeeklyTooltipProps {
  active?: boolean;
  payload?: WeeklyTooltipPayloadEntry[];
  label?: string;
  weekMeta: Record<string, WeekMetaEntry>;
  plannedKey: string;
  realizedKey: string;
}

/** Custom tooltip (isti princip kao Management-ov MonthlyTooltip) — samo kvadratić + iznos po
 * seriji (bez labela, već postoje u legendi iznad), pa tjedan ispod. */
const WeeklyTooltip = ({
  active,
  payload,
  label,
  weekMeta,
  plannedKey,
  realizedKey
}: WeeklyTooltipProps) => {
  if (!active || !payload?.length || !label) return null;
  const meta = weekMeta[label];
  const plannedValue = payload.find((p) => p.dataKey === plannedKey)?.value ?? 0;
  const realizedValue = payload.find((p) => p.dataKey === realizedKey)?.value ?? 0;
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-soft)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Stack gap={4}>
        <Group gap={6} wrap="nowrap">
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: 'var(--chart-planned)',
              flexShrink: 0
            }}
          />
          <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {formatHours(plannedValue)}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap">
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: 'var(--chart-collected)',
              flexShrink: 0
            }}
          />
          <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {formatHours(realizedValue)}
          </Text>
        </Group>
        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
          {meta?.weekYear}
        </Text>
      </Stack>
    </div>
  );
};

export const BoardPmPage = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<string | null>('LAST_6_MONTHS');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [contractId, setContractId] = useState<string | null>(null);
  const [objectId, setObjectId] = useState<string | null>(null);
  const isCustomPeriod = period === 'CUSTOM';

  const { data } = useGetBoardPmSummary({
    period: period ?? undefined,
    fromDate: isCustomPeriod ? (dateRange[0] ?? undefined) : undefined,
    toDate: isCustomPeriod ? (dateRange[1] ?? undefined) : undefined,
    contractId: contractId ?? undefined,
    objectId: objectId ?? undefined
  });

  const { data: contractsData } = useListContracts();
  const contractOptions = useMemo(
    () =>
      ((contractsData as Contract[] | undefined) ?? [])
        .filter((c) => c.id)
        .map((c) => ({ value: c.id as string, label: c.contractNumber ?? c.name ?? '' })),
    [contractsData]
  );

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive && object.id)
        .map((object) => ({
          value: object.id as string,
          label: formatObjectLabel(object)
        })),
    [objectsData, t]
  );

  const plannedHours = data?.plannedHours ?? 0;
  const realizedHours = data?.realizedHours ?? 0;
  const realizationPercent = plannedHours > 0 ? (realizedHours / plannedHours) * 100 : 0;

  const weeklyData = (data?.weeklyHours ?? []).map((w) => ({
    weekKey: `${w.reportYear}-${String(w.weekNumber).padStart(2, '0')}`,
    [t('board.chart.planned')]: w.plannedHours ?? 0,
    [t('board.chart.realized')]: w.realizedHours ?? 0
  }));
  const weekMeta = Object.fromEntries(
    (data?.weeklyHours ?? []).map((w) => {
      const planned = w.plannedHours ?? 0;
      const realized = w.realizedHours ?? 0;
      return [
        `${w.reportYear}-${String(w.weekNumber).padStart(2, '0')}`,
        {
          label: t('board.chart.weekLabel', { week: w.weekNumber }),
          percentLabel: planned > 0 ? `${Math.round((realized / planned) * 100)} %` : '—',
          weekYear: t('board.chart.weekYearLabel', { week: w.weekNumber, year: w.reportYear })
        }
      ];
    })
  );

  // Trend "↓/↑ X pp od tj. N" u Realizacija KPI-ju — usporedba PRVOG i ZADNJEG tjedna u trenutno
  // prikazanom grafu (ista ideja kao Figma "↓ 16 pp od tj. 33": Tj.33=84% → Tj.38=68% = pad 16pp).
  // Nije posebna backend agregacija — izvedeno iz istog weeklyHours niza koji graf već prikazuje.
  const weeklyHoursList = data?.weeklyHours ?? [];
  const weekPercent = (w: { plannedHours?: number; realizedHours?: number }) =>
    (w.plannedHours ?? 0) > 0 ? ((w.realizedHours ?? 0) / (w.plannedHours ?? 1)) * 100 : 0;
  const firstWeek = weeklyHoursList[0];
  const lastWeek = weeklyHoursList[weeklyHoursList.length - 1];
  const trendDelta =
    firstWeek && lastWeek && weeklyHoursList.length > 1
      ? Math.round(weekPercent(firstWeek) - weekPercent(lastWeek))
      : 0;
  const realizationCaption =
    trendDelta !== 0 && firstWeek
      ? `${t('board.kpiRealizationCaption', { realized: formatHours(realizedHours), planned: formatHours(plannedHours) })} · ${t(
          'board.kpiRealizationTrend',
          {
            arrow: trendDelta > 0 ? '↓' : '↑',
            delta: Math.abs(trendDelta),
            week: firstWeek.weekNumber
          }
        )}`
      : t('board.kpiRealizationCaption', {
          realized: formatHours(realizedHours),
          planned: formatHours(plannedHours)
        });

  // Jedan redak po statusu (label + traka + "{{count}} · {{percent}}%"), isti vizualni obrazac kao
  // Management "Realizacija po Ugovoru" — ne stacked bar chart. Boje su fiksne po statusu (ne po
  // pragu postotka kao kod realizationRowColor).
  const wpStatusCounts = data?.workPackageStatusCounts ?? [];
  const wpStatusTotal = wpStatusCounts.reduce((sum, c) => sum + (c.count ?? 0), 0);
  const wpStatusRows = WP_STATUS_ORDER.map((status) => {
    const count = wpStatusCounts.find((c) => c.status === status)?.count ?? 0;
    const percent = wpStatusTotal > 0 ? Math.round((count / wpStatusTotal) * 100) : 0;
    return { status, count, percent };
  });

  const weeklyHoursPanel = (
    <CardSection>
      <div style={{ padding: 20 }}>
        <Group justify="space-between" align="center" mb={16} wrap="nowrap">
          <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {t('board.chart.weeklyTitle')}
          </Text>
          <Group gap={12} wrap="nowrap">
            <Group gap={14} wrap="nowrap">
              <Group gap={6} wrap="nowrap">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: 'var(--chart-planned)',
                    flexShrink: 0
                  }}
                />
                <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                  {t('board.chart.planned')}
                </Text>
              </Group>
              <Group gap={6} wrap="nowrap">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: 'var(--chart-collected)',
                    flexShrink: 0
                  }}
                />
                <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                  {t('board.chart.realized')}
                </Text>
              </Group>
            </Group>
            <PanelInfoIcon label={t('board.chart.weeklyInfo')} />
          </Group>
        </Group>
        {weeklyData.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('board.chart.empty')}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div style={{ width: `max(100%, ${weeklyData.length * WEEK_MIN_WIDTH}px)` }}>
              <BarChart
                h={280}
                data={weeklyData}
                dataKey="weekKey"
                maxBarWidth={22}
                cursorFill="transparent"
                series={[
                  { name: t('board.chart.planned'), color: 'var(--chart-planned)' },
                  { name: t('board.chart.realized'), color: 'var(--chart-collected)' }
                ]}
                withLegend={false}
                xAxisProps={{
                  height: 40,
                  tickLine: false,
                  tick: (props) => <WeekTick {...props} weekMeta={weekMeta} />
                }}
                tooltipProps={{
                  content: (props) => (
                    <WeeklyTooltip
                      {...props}
                      weekMeta={weekMeta}
                      plannedKey={t('board.chart.planned')}
                      realizedKey={t('board.chart.realized')}
                    />
                  )
                }}
                valueFormatter={(value) => `${value} h`}
              />
            </div>
          </div>
        )}
        <Text fz={12} mt={12} style={{ color: 'var(--color-text-muted)' }}>
          {t('board.chart.weeklyCaption')}
        </Text>
      </div>
    </CardSection>
  );

  const workPackageStatusPanel = (
    <CardSection>
      <div style={{ padding: 20 }}>
        <Group justify="space-between" align="center" mb={16} wrap="nowrap">
          <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {t('board.chart.wpStatusTitle')}
          </Text>
          <PanelInfoIcon
            label={t('board.chart.wpStatusInfo')}
            title={t('board.chart.wpStatusTitle')}
          />
        </Group>
        {wpStatusCounts.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('board.chart.empty')}
          </Text>
        ) : (
          <Stack gap={16}>
            {wpStatusRows.map((row) => (
              <Group key={row.status} gap={12} wrap="nowrap" align="center">
                <Text
                  fz={13}
                  style={{ color: 'var(--color-text-secondary)', width: 72, flexShrink: 0 }}
                >
                  {t(workPackageStatusLabelKey(row.status))}
                </Text>
                <div style={{ flex: 1, height: 14, minWidth: 1, position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${Math.min(Math.max(row.percent, 0), 100)}%`,
                      backgroundColor: WP_STATUS_COLORS[row.status],
                      borderTopRightRadius: 4,
                      borderBottomRightRadius: 4
                    }}
                  />
                </div>
                <Text
                  fz={13}
                  style={{
                    color: 'var(--color-text-primary)',
                    width: 80,
                    flexShrink: 0,
                    textAlign: 'right'
                  }}
                >
                  {t('board.chart.wpStatusValue', { count: row.count, percent: row.percent })}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
        <Text fz={12} mt={12} style={{ color: 'var(--color-text-muted)' }}>
          {t('board.chart.wpStatusCaption', {
            total: wpStatusTotal,
            activeCount: data?.activeWorkPackages ?? 0
          })}
        </Text>
      </div>
    </CardSection>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Stack gap={4}>
          <Group gap={10} align="center">
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {t('board.title')}
            </Title>
            <Badge variant="light" color="gray">
              {t('board.pmBadge')}
            </Badge>
          </Group>
          <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
            {t('board.pmSubtitle')}
          </Text>
        </Stack>

        <Box hiddenFrom="sm" w="100%">
          <MobileListFilters
            filters={[
              {
                key: 'period',
                label: t('board.filterPeriod'),
                options: buildPeriodOptions(),
                value: period,
                onChange: (value) => setPeriod(value ?? 'LAST_6_MONTHS'),
                emptyValue: 'LAST_6_MONTHS',
                variant: 'buttons',
                dateRange: {
                  showWhen: 'CUSTOM',
                  label: t('board.filterDateRange'),
                  value: dateRange,
                  onChange: setDateRange
                }
              },
              {
                key: 'contract',
                label: t('board.filterContract'),
                options: contractOptions,
                value: contractId,
                onChange: setContractId
              },
              {
                key: 'object',
                label: t('board.filterObject'),
                options: objectOptions,
                value: objectId,
                onChange: setObjectId
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <div style={{ width: 200 }}>
            <SelectField
              label={t('board.filterPeriod')}
              data={buildPeriodOptions()}
              value={period}
              onChange={(value) => {
                setPeriod(value);
                if (value !== 'CUSTOM') setDateRange([null, null]);
              }}
              allowDeselect={false}
              leftSection={<IconFigmaCalendar size={16} />}
            />
          </div>
          {isCustomPeriod && (
            <div style={{ width: 260 }}>
              <DateRangeField
                label={t('board.filterDateRange')}
                placeholder={t('board.filterDateRange')}
                hideCalendarIcon
                valueFormat="DD.MM.YYYY."
                value={dateRange}
                onChange={setDateRange}
                clearable
              />
            </div>
          )}
          <div style={{ width: 220 }}>
            <SelectField
              label={t('board.filterContract')}
              placeholder={t('board.filterContractPlaceholder')}
              data={contractOptions}
              value={contractId}
              onChange={setContractId}
              clearable
            />
          </div>
          <div style={{ width: 220 }}>
            <SelectField
              label={t('board.filterObject')}
              placeholder={t('board.filterObjectPlaceholder')}
              data={objectOptions}
              value={objectId}
              onChange={setObjectId}
              clearable
            />
          </div>
        </Group>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={{ base: 10, sm: 16 }}>
          <BoardKpiCard
            icon={<IconFigmaFile size={16} />}
            label={t('board.kpiActiveContracts')}
            value={data?.activeContracts ?? 0}
            caption={
              (data?.contractsBelowPlan ?? 0) > 0
                ? t('board.kpiBelowPlanCaption', { count: data?.contractsBelowPlan })
                : undefined
            }
            captionColor="var(--color-status-danger)"
            info={t('board.kpiActiveContractsInfoPm')}
          />
          <BoardKpiCard
            icon={<IconFigmaStructure size={16} />}
            label={t('board.kpiActiveWorkPackages')}
            value={data?.activeWorkPackages ?? 0}
            caption={
              (data?.lateWorkPackages ?? 0) > 0
                ? t('board.kpiLateCaption', { count: data?.lateWorkPackages })
                : undefined
            }
            captionColor="var(--color-status-danger)"
            info={t('board.kpiActiveWorkPackagesInfo')}
          />
          <BoardKpiCard
            icon={<IconFigmaCheckCircle size={16} />}
            label={t('board.kpiRealizationToDate')}
            value={`${Math.round(realizationPercent)} %`}
            caption={realizationCaption}
            progressPercent={realizationPercent}
            info={t('board.kpiRealizationInfoPm')}
          />
          <BoardKpiCard
            icon={<IconFigmaCalendar size={16} />}
            label={t('board.kpiOpenWeekReports')}
            value={data?.openWeekReports ?? 0}
            caption={
              (data?.weekReportsReturnedForRevision ?? 0) > 0
                ? t('board.kpiReturnedCaption', { count: data?.weekReportsReturnedForRevision })
                : undefined
            }
            captionColor="var(--color-status-danger)"
            info={t('board.kpiOpenWeekReportsInfo')}
          />
        </SimpleGrid>

        {/* Mobile — oba panela puna širina, naslagana (isto kao BoardManagementPage); 7fr/4fr u
            istom retku je na mobitelu premalo za čitljiv graf. */}
        <Stack gap={16} hiddenFrom="sm" w="100%">
          {weeklyHoursPanel}
          {workPackageStatusPanel}
        </Stack>

        {/* Omjer 7fr/4fr (≈63/37), isti kao Management "Charts Row" — vidi BoardManagementPage. */}
        <Box visibleFrom="sm" w="100%">
          <div style={{ display: 'grid', gridTemplateColumns: '7fr 4fr', gap: 16 }}>
            {weeklyHoursPanel}
            {workPackageStatusPanel}
          </div>
        </Box>

        <ContractRealizationTable
          title={t('board.table.title')}
          info={t('board.table.pmInfo')}
          rows={data?.contracts ?? []}
        />
      </Stack>
    </Container>
  );
};
